// components/PublicNavbar.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import LoginIcon from "@mui/icons-material/Login";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Logo from "../assets/Header/techlogo2.png";
import AuthModal from "./AuthModal";

const navLinks = [
  {
    text: "Home",
    path: "/",
    icon: <SchoolIcon fontSize="small" />,
    color: "#2BAD65",
  },
  {
    text: "Courses",
    path: "#courses",
    icon: <MenuBookIcon fontSize="small" />,
    color: "#2BAD65",
  },
  {
    text: "About",
    path: "/about",
    icon: <InfoIcon fontSize="small" />,
    color: "#2BAD65",
  },
  {
    text: "Contact",
    path: "/contact",
    icon: <ContactMailIcon fontSize="small" />,
    color: "#2BAD65",
  },
];

const PublicNavbar = ({ scrollToCourses }) => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("/");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
    handleCloseNavMenu();
  };

  const handleCloseAuthModal = () => {
    setAuthModalOpen(false);
  };

  const handleNavClick = (path) => {
    if (path === "#courses") {
      if (window.location.pathname === "/") {
        if (scrollToCourses) {
          scrollToCourses();
        }
      } else {
        navigate("/");
        setTimeout(() => {
          if (scrollToCourses) {
            scrollToCourses();
          }
        }, 100);
      }
    } else {
      navigate(path);
    }
    handleCloseNavMenu();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled ? "#ffffffcc" : "#fff",
          backdropFilter: scrolled ? "blur(20px)" : "blur(10px)",
          borderBottom: scrolled
            ? "1px solid rgba(43, 173, 101, 0.1)"
            : "1px solid rgba(43, 173, 101, 0.05)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: scrolled ? "0 4px 30px rgba(43, 173, 101, 0.08)" : "none",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: { xs: 64, md: 80 } }}>
            {/* Logo - Desktop */}
            <Box
              component={NavLink}
              to="/"
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                textDecoration: "none",
                flexShrink: 0,
                gap: 1,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -4,
                  left: 0,
                  width: "100%",
                  height: 2,
                  background: "linear-gradient(90deg, #2BAD65, #A0EF48)",
                  transform: "scaleX(0)",
                  transformOrigin: "right",
                  transition: "transform 0.3s ease",
                },
                "&:hover::after": {
                  transform: "scaleX(1)",
                  transformOrigin: "left",
                },
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="Wavepalm Techub"
                sx={{
                  height: 48,
                  width: "auto",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              />
              <Chip
                label="Premium"
                size="small"
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.12)",
                  color: "#2BAD65",
                  fontWeight: 700,
                  fontSize: "0.6rem",
                  height: 20,
                  border: "1px solid rgba(43, 173, 101, 0.2)",
                  ml: 1,
                }}
              />
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ display: { xs: "flex", md: "none" }, flex: 1 }}>
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{
                  color: "#2BAD65",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Logo - Mobile */}
            <Box
              component={NavLink}
              to="/"
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                textDecoration: "none",
                flexGrow: 1,
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="Wavepalm Techub"
                sx={{
                  height: 35,
                  width: "auto",
                }}
              />
            </Box>

            {/* Mobile Menu Drawer */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              PaperProps={{
                sx: {
                  width: "100%",
                  maxWidth: 300,
                  height: "100vh",
                  backgroundColor: "#F7F8F2",
                  borderRadius: 0,
                  border: "none",
                  p: 3,
                  position: "relative",
                },
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleCloseNavMenu}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "rgba(43, 173, 101, 0.5)",
                  "&:hover": {
                    color: "#2BAD65",
                    backgroundColor: "rgba(43, 173, 101, 0.05)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Mobile Logo */}
              <Box sx={{ mb: 4, mt: 2 }}>
                <Box
                  component="img"
                  src={Logo}
                  alt="Wavepalm Techub"
                  sx={{
                    height: 40,
                    width: "auto",
                  }}
                />
              </Box>

              {/* Mobile Nav Links */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {navLinks.map((link) => (
                  <MenuItem
                    key={link.text}
                    onClick={() => handleNavClick(link.path)}
                    sx={{
                      py: 1.5,
                      px: 3,
                      color: activeLink === link.path ? "#2BAD65" : "#333333",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      backgroundColor:
                        activeLink === link.path
                          ? "rgba(43, 173, 101, 0.08)"
                          : "transparent",
                      border:
                        activeLink === link.path
                          ? "1px solid rgba(43, 173, 101, 0.2)"
                          : "1px solid transparent",
                      "&:hover": {
                        backgroundColor: "rgba(43, 173, 101, 0.05)",
                        transform: "translateX(8px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ color: "#2BAD65" }}>{link.icon}</Box>
                      <Typography fontWeight={500}>{link.text}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Box>

              <Box
                sx={{
                  mt: "auto",
                  pt: 3,
                  borderTop: "1px solid rgba(43, 173, 101, 0.08)",
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleAuthClick("login")}
                  startIcon={<LoginIcon />}
                  sx={{
                    mb: 1.5,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    backgroundColor: "#2BAD65",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#239A56",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(43, 173, 101, 0.3)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleAuthClick("signup")}
                  startIcon={<HowToRegIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#2BAD65",
                    borderColor: "rgba(43, 173, 101, 0.3)",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#2BAD65",
                      backgroundColor: "rgba(43, 173, 101, 0.05)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </Menu>

            {/* Desktop Navigation */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {navLinks.map((link) => (
                <Button
                  key={link.text}
                  onClick={() => handleNavClick(link.path)}
                  startIcon={link.icon}
                  sx={{
                    color:
                      activeLink === link.path
                        ? "#2BAD65"
                        : "rgba(51, 51, 51, 0.7)",
                    px: 2.5,
                    py: 1,
                    fontWeight: activeLink === link.path ? 600 : 500,
                    borderRadius: 2,
                    position: "relative",
                    transition: "all 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      bottom: 4,
                      left: "50%",
                      transform: "translateX(-50%) scaleX(0)",
                      width: "70%",
                      height: 2,
                      background: "linear-gradient(90deg, #2BAD65, #A0EF48)",
                      borderRadius: 2,
                      transition: "transform 0.3s ease",
                    },
                    "&:hover": {
                      color: "#2BAD65",
                      backgroundColor: "rgba(43, 173, 101, 0.05)",
                    },
                    "&:hover::before": {
                      transform: "translateX(-50%) scaleX(1)",
                    },
                    "&.active": {
                      color: "#2BAD65",
                      backgroundColor: "rgba(43, 173, 101, 0.08)",
                    },
                    "&.active::before": {
                      transform: "translateX(-50%) scaleX(1)",
                    },
                  }}
                  className={activeLink === link.path ? "active" : ""}
                >
                  {link.text}
                </Button>
              ))}
            </Box>

            {/* Desktop Auth Buttons */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Button
                variant="text"
                onClick={() => handleAuthClick("login")}
                startIcon={<LoginIcon />}
                sx={{
                  fontWeight: 500,
                  color: "rgba(51, 51, 51, 0.7)",
                  px: 2,
                  "&:hover": {
                    color: "#2BAD65",
                    backgroundColor: "rgba(43, 173, 101, 0.05)",
                  },
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAuthClick("signup")}
                startIcon={<HowToRegIcon />}
                sx={{
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #2BAD65, #A0EF48)",
                  boxShadow: "0 4px 20px rgba(43, 173, 101, 0.3)",
                  color: "#ffffff",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 30px rgba(43, 173, 101, 0.4)",
                    background: "linear-gradient(135deg, #239A56, #8EDB40)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Started
              </Button>

              {/* Premium Badge */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  ml: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  borderRadius: 50,
                  border: "1px solid rgba(43, 173, 101, 0.15)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#2BAD65",
                    animation: "pulse 2s infinite",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    letterSpacing: 0.5,
                  }}
                >
                  PRO
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={handleCloseAuthModal}
        mode={authMode}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(0.8);
            }
          }
        `}
      </style>
    </>
  );
};

export default PublicNavbar;
